import { useMemo, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useSearchParams } from "react-router-dom";

import { Button } from "~/components";
import { providersFilters, useQueryBuilder } from "~/hooks";
import { MainLayout, MODAL_ROUTES, useNavigateModal } from "~/router";
import { adminProviders } from "~/services";
import { dateToUsFormat } from "~/shared/utils";
import { icons, PaginatedTable } from "~/ui";
import { mapLaravelPagination, mapProviderToTableRow } from "~/utils";
import {
  DropdownInput,
  DropdownOption,
  SearchInput,
  StatusBadge,
  Title,
} from "../components";
import { GlobalRulesPanel } from "../global-schedule-rules/global-rules-panel";
import { usePermissions } from "../hooks/usePermissions";
import { options, ProviderSchedule } from "./constants";

export const columns: ColumnDef<ProviderSchedule>[] = [
  {
    header: "Clinician Name",
    accessorKey: "name",
  },
  {
    header: "Schedule Status",
    accessorKey: "scheduleAvailable",
    cell: ({ row }) => (
      <StatusBadge isAvailable={row.original.scheduleAvailable} />
    ),
  },
  {
    header: "Booking Conflicts",
    accessorKey: "bookingConflicts",
  },
  {
    header: "Schedule Last Modified Date (ET)",
    accessorKey: "scheduleLastModifiedAt",
    cell: ({ row }) => {
      const date = row.original.scheduleLastModifiedAt;
      if (!date) return "-";
      return dateToUsFormat(date);
    },
  },
];

export const ProviderDashboard = () => {
  const [searchParams] = useSearchParams();
  const navigateModal = useNavigateModal();
  const { canManageGlobalScheduleRules } = usePermissions();
  const [showGlobalRules, setShowGlobalRules] = useState(false);
  const [, setGlobalRulesCount] = useState(0);

  const querySearch = searchParams.get("search") ?? "";
  const queryHasSchedule = searchParams.get("hasSchedule") ?? "";

  const selectedFilter =
    queryHasSchedule && options.find((op) => op.id === queryHasSchedule);

  const [selected, setSelected] = useState<DropdownOption>(
    selectedFilter || options[0],
  );
  const [search, setSearch] = useState(decodeURIComponent(querySearch));

  const {
    builder: providersBuilder,
    setCurrentPage,
    setSorting,
    sortingState,
    setFilters,
  } = useQueryBuilder({
    builderFilters: [
      {
        attribute: "hasSchedule",
        value: [queryHasSchedule],
      },
      {
        attribute: "search",
        value: [querySearch],
      },
    ],
    aliases: providersFilters,
  });

  const { data, isLoading } = adminProviders.useAdminProvidersQuery({
    queryBuilder: providersBuilder,
  });

  const rows = useMemo(
    () => data?.data.map(mapProviderToTableRow) ?? [],
    [data],
  );

  const pagination = useMemo(() => {
    if (!data) return undefined;
    return mapLaravelPagination(data.meta, data.links);
  }, [data]);

  const handleRoleFilter = (option: DropdownOption) => {
    setSelected(option);
    setCurrentPage(1);
    setFilters([{ attribute: "hasSchedule", value: option.id }]);
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentPage(1);
    setFilters([
      {
        attribute: "search",
        value: [encodeURIComponent(search)],
      },
    ]);
  };

  return (
    <MainLayout>
      <header className="flex flex-col justify-between pb-8 md:flex-row">
        <h1 className="text-indigo font-ivy text-[32px]">
          Clinician Dashboard
        </h1>

        <div className="flex flex-col gap-4 md:flex-row">
          <SearchInput
            setSearch={setSearch}
            search={search}
            handleOnSubmit={handleSearch}
            placeholder="Search for clinicians..."
          />
        </div>
      </header>

      <div className="flex items-center justify-between pb-4">
        <Title
          title="Clinicians"
          titleClassName="text-indigo"
          className="w-auto"
        />

        <div className="flex items-center gap-4 whitespace-nowrap">
          {canManageGlobalScheduleRules && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowGlobalRules(true)}
              className="text-indigo gap-1.5 px-0 font-sans text-lg font-normal"
            >
              <span>Global Rules</span>
              <icons.outline.PencilIcon className="size-5" />
            </Button>
          )}

          <DropdownInput
            options={options}
            label={(option) => `Showing ${option.label}`}
            selected={selected}
            handleOnChange={handleRoleFilter}
          />
        </div>
      </div>

      <hr className="border-soft-pastel-lavender" />

      <div className="pt-3">
        <PaginatedTable
          data={rows}
          columns={columns}
          sorting={sortingState}
          setSorting={setSorting}
          setCurrentPage={setCurrentPage}
          isLoadingData={isLoading}
          pagination={pagination}
          onRowClick={(row) =>
            navigateModal(`${MODAL_ROUTES.providerDetails}/${row.id}`)
          }
        />
      </div>

      {canManageGlobalScheduleRules && (
        <GlobalRulesPanel
          show={showGlobalRules}
          onClose={() => setShowGlobalRules(false)}
          onRulesCountChange={setGlobalRulesCount}
        />
      )}
    </MainLayout>
  );
};
